import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Tabs, Tab, Box, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { isEqual } from 'lodash-es';
import { Dialog } from '../../components/Dialog';
import { EarlyAdjustmentsTab } from './EarlyAdjustmentsTab';
import { LateAdjustmentsTab } from './LateAdjustmentsTab';
import { useAlerts } from '../../hooks/use-alerts';
import { Spinner } from '../../components/Spinner';
import { usePermission } from '../../hooks/use-permission';

const TABS_CONFIG = [
  { type: 'early', Component: EarlyAdjustmentsTab },
  { type: 'late', Component: LateAdjustmentsTab },
];

export function FieldDetails(props) {
  const { field, handleClose, handleSave } = props;
  const { showError } = useAlerts();
  const canAdjust = usePermission('RUN_RX');

  const earlyReverter = useRef(() => {});
  const lateReverter = useRef(() => {});

  const reverters = useMemo(
    () => ({ early: earlyReverter, late: lateReverter }),
    [],
  );

  const loadingCount = useRef(TABS_CONFIG.length);
  const [isLoading, setLoading] = useState(TABS_CONFIG.length);
  const [tabs, setTabs] = useState([]);
  const [currentTab, setCurrentTab] = useState(null);
  const [data, setData] = useState({});
  const [isSaving, setSaving] = useState(false);

  const getCurrentAdjustments = useCallback(
    (type) => {
      const dataForType = data[type];
      if (!dataForType) return [];
      const { fieldAdjustment, groups } = data[type].changed;
      return [fieldAdjustment, ...groups.map((g) => g.adjustment)];
    },
    [data],
  );

  const canRevert = useMemo(
    () => getCurrentAdjustments(currentTab).some(Boolean),
    [currentTab, getCurrentAdjustments],
  );

  const canSave = useMemo(
    () =>
      Object.values(data).some(({ base, changed }) => !isEqual(base, changed)),
    [data],
  );

  const revertChanges = useCallback(() => {
    reverters[currentTab].current();
  }, [currentTab, reverters]);

  const saveChanges = useCallback(() => {
    const groupMapper = (g) => ({
      maxValue: g.value,
      adjustmentFactor: g.adjustment,
    });
    setSaving(true);

    handleSave(
      field.aaManagedId,
      data.early?.changed?.fieldAdjustment,
      data.late?.changed?.fieldAdjustment,
      data.early?.changed?.groups?.map(groupMapper),
      data.late?.changed?.groups?.map(groupMapper),
    )
      .catch(showError)
      .finally(() => setSaving(false));
  }, [data, field, handleSave, showError]);

  const actions = useMemo(
    () =>
      (isLoading && loadingCount.current) || !canAdjust
        ? false
        : [
            <Button
              key="cancel"
              onClick={handleClose}
              color="inherit"
              variant="contained"
              disabled={isSaving}
            >
              Close
            </Button>,
            <Button
              key="revert"
              onClick={revertChanges}
              color="inherit"
              variant="contained"
              disabled={!canRevert || isSaving}
            >
              Revert
            </Button>,
            <LoadingButton
              key="save"
              onClick={saveChanges}
              color="primary"
              variant="contained"
              loading={isSaving}
              disabled={!canSave}
            >
              Save
            </LoadingButton>,
          ],
    [
      isLoading,
      handleClose,
      revertChanges,
      canRevert,
      saveChanges,
      isSaving,
      canSave,
      canAdjust,
    ],
  );

  const tabLoaded = useCallback((tab, groups, fieldAdjustment) => {
    if (loadingCount.current > 0) loadingCount.current -= 1;
    setLoading((current) => current - 1);

    if (!groups) return;

    setTabs((current) => [...current, tab]);
    setData((current) => ({
      ...current,
      [tab]: {
        base: { groups, fieldAdjustment },
        changed: { groups, fieldAdjustment },
      },
    }));
  }, []);

  const groupsChanged = useCallback((tab, groups) => {
    setData((current) => {
      const cloned = structuredClone(current);
      cloned[tab].changed.groups = groups;
      return cloned;
    });
  }, []);

  const fieldAdjustmentChanged = useCallback((tab, adjustment) => {
    setData((current) => {
      const cloned = structuredClone(current);
      cloned[tab].changed.fieldAdjustment = adjustment;
      return cloned;
    });
  }, []);

  useEffect(() => {
    if (!isLoading && !currentTab) {
      const first = TABS_CONFIG.find(({ type }) => tabs.includes(type));
      if (first) setCurrentTab(first.type);
    }
  }, [currentTab, isLoading, tabs]);

  return (
    <Dialog title={field.fieldName} onCancel={handleClose} actions={actions}>
      <Box sx={{ minHeight: 500 }}>
        {loadingCount.current ? <Spinner sx={{ minHeight: 500 }} /> : null}
        {buildTabs()}
        {buildTabContent()}
      </Box>
    </Dialog>
  );

  function buildTabs() {
    return currentTab ? (
      <Tabs value={currentTab} onChange={(e, tab) => setCurrentTab(tab)}>
        {tabs.includes('early') && <Tab label="Early" value="early" />}
        {tabs.includes('late') && <Tab label="Late" value="late" />}
      </Tabs>
    ) : null;
  }

  function buildTabContent() {
    return TABS_CONFIG.map(({ type, Component }) => (
      <Component
        field={field}
        isVisible={currentTab === type}
        key={type}
        onClose={handleClose}
        onFieldAdjustment={fieldAdjustmentChanged}
        onGroupsAdjustment={groupsChanged}
        onLoad={tabLoaded}
        revertRef={reverters[type]}
      />
    ));
  }
}
